import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'

const PAGE_LIMIT = 20
const DEFAULT_SORT = { by: 'created_at', order: 'desc' }

// GET requests go through axios.get(url, { headers }) — query params must be
// embedded in the URL itself, they aren't sent from a request body.
function buildQuery(filters, sort, page) {
  const parts = [`page[number]=${page}`, `page[limit]=${PAGE_LIMIT}`, `sort_by=${sort.by}`, `sort_order=${sort.order}`]
  if (filters.search) parts.push(`search=${encodeURIComponent(filters.search)}`)
  if (filters.actorId) parts.push(`actor_id=${encodeURIComponent(filters.actorId)}`)
  filters.actions.forEach((value) => parts.push(`actions[]=${encodeURIComponent(value)}`))
  return parts.join('&')
}

export const useActivityLogsStore = defineStore('activityLogs', {
  state: () => ({
    organizationId: null,
    logs: [],
    pageInfo: { current_page: 0, total_pages: 0, total_records: 0, limit: PAGE_LIMIT },
    filters: { search: '', actions: [], actorId: null },
    sort: { ...DEFAULT_SORT },
    loading: false,
    loadingMore: false,
    error: null,
    fetchedAt: null,
    // Distinct action values this org has ever logged — powers the action
    // filter dropdown with only choices that could actually return results,
    // rather than a fixed list baked into the frontend.
    availableActions: [],
    availableActionsLoading: false,
  }),

  getters: {
    hasActiveFilters: (state) => !!state.filters.search || state.filters.actions.length > 0 || !!state.filters.actorId,
    hasMore: (state) => state.pageInfo.current_page === 0 || state.pageInfo.current_page < state.pageInfo.total_pages,
  },

  actions: {
    // Unlike most page stores in this app, this one is not cached across
    // visits — an audit log is meant to always show what's happened *up to
    // right now*, so every time the page is opened (including navigating
    // away and back within the same session) this re-hits the API rather
    // than reusing whatever was fetched last time.
    async init(organizationId) {
      const isNewOrg = this.organizationId !== organizationId
      this.organizationId = organizationId
      if (isNewOrg) {
        this.filters = { search: '', actions: [], actorId: null }
        this.sort = { ...DEFAULT_SORT }
      }
      await Promise.all([this.fetchPage(true), this.fetchAvailableActions()])
    },

    async setSort(by, order) {
      this.sort = { by, order }
      await this.fetchPage(true)
    },

    async setSearch(value) {
      this.filters.search = value
      await this.fetchPage(true)
    },

    async toggleAction(value) {
      const idx = this.filters.actions.indexOf(value)
      if (idx === -1) this.filters.actions.push(value)
      else this.filters.actions.splice(idx, 1)
      await this.fetchPage(true)
    },

    async clearActionFilters() {
      this.filters.actions = []
      await this.fetchPage(true)
    },

    async clearFilters() {
      this.filters = { search: '', actions: [], actorId: null }
      await this.fetchPage(true)
    },

    async fetchMore() {
      if (this.loadingMore || this.loading || !this.hasMore) return
      await this.fetchPage(false)
    },

    async fetchPage(reset) {
      if (reset) {
        this.loading = true
        this.error = null
      } else {
        this.loadingMore = true
      }

      const nextPage = reset ? 1 : this.pageInfo.current_page + 1
      const query = buildQuery(this.filters, this.sort, nextPage)

      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/organizations/${this.organizationId}/activity_logs?${query}`, {}, false)
        if (!res.js_error && res.response.success) {
          const { records, page } = res.response.data
          this.logs = reset ? records : [...this.logs, ...records]
          this.pageInfo = page
          this.fetchedAt = Date.now()
        } else {
          this.error = 'Failed to load activity logs.'
        }
      } catch {
        this.error = 'Something went wrong.'
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    async fetchAvailableActions() {
      this.availableActionsLoading = true
      try {
        const { axiosRequest } = useRequest()
        const res = await axiosRequest('GET', `/api/v1/organizations/${this.organizationId}/activity_logs/actions`, {}, false)
        if (!res.js_error && res.response.success) this.availableActions = res.response.data
      } finally {
        this.availableActionsLoading = false
      }
    },
  },
})
